<template>
  <div class="w-full mx-auto p-6 bg-white shadow rounded">
    <h2 class="text-2xl font-bold mb-6">Create Career</h2>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <label class="block font-semibold mb-1">Title *</label>
        <input
          v-model="form.title"
          type="text"
          placeholder="Enter job title"
          class="w-full border border-gray-300 rounded px-3 py-2 focus:ring focus:ring-blue-400 focus:border-blue-400"
        />
      </div>

      <div>
        <label class="block font-semibold mb-1">Location *</label>
        <input
          v-model="form.location"
          type="text"
          placeholder="e.g. Jakarta, Indonesia"
          class="w-full border border-gray-300 rounded px-3 py-2 focus:ring focus:ring-blue-400 focus:border-blue-400"
        />
      </div>

      <div class="lg:col-span-2">
        <label class="block font-semibold mb-1">Work Type *</label>
        <select
          v-model="form.work_type"
          class="w-full border border-gray-300 rounded px-3 py-2 focus:ring focus:ring-blue-400 focus:border-blue-400"
        >
          <option value="">Select work type</option>
          <option value="Remote">Remote</option>
          <option value="Hybrid">Hybrid</option>
          <option value="Onsite">Onsite</option>
        </select>
      </div>

      <div class="lg:col-span-2">
        <label class="block font-semibold mb-1">Rentang Tanggal *</label>
        <div class="date-range relative text-xs">
          <div class="absolute left-3 top-1/2 transform -translate-y-1/2 z-1">
            <Calendar color="neutral-700" />
          </div>
          <date-range-picker
            v-model="dateRange"
            :locale-data="{ format: 'dd/mm/yyyy', separator: ' - ' }"
            @update="handleDateRangeChange"
            :showDropdowns="false"
            :timePicker="true"
            :timePicker24Hour="true"
            :ranges="false"
          ></date-range-picker>
        </div>
      </div>
    </div>

    <div class="mt-6">
      <label class="block font-semibold mb-1">Description *</label>
      <div class="border border-gray-300 rounded">
        <TextEditorComponents v-model="form.description" />
      </div>
    </div>

    <div class="flex gap-3 mt-6">
      <button
        @click="submit"
        class="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded shadow transition duration-200"
      >
        Create Career
      </button>
      <button
        @click="$router.push('/career')"
        class="bg-gray-300 hover:bg-gray-400 text-gray-700 font-bold py-2 px-6 rounded shadow transition duration-200"
      >
        Cancel
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex"
import dayjs from "dayjs"
import "vue2-daterange-picker/dist/vue2-daterange-picker.css"
import TextEditorComponents from "@/components/Forms/TextEditorComponents"

export default {
  name: "CareerFormCreate",
  components: {
    TextEditorComponents,
    DateRangePicker: () => import("vue2-daterange-picker"),
    Calendar: () => import("@/components/Icons/Calendar")
  },
  data() {
    const NOW = new Date()
    return {
      form: {
        title: "",
        location: "",
        work_type: "",
        description: ""
      },
      dateRange: {
        startDate: NOW,
        endDate: new Date(NOW.getTime() + 30 * 24 * 60 * 60 * 1000)
      }
    }
  },
  methods: {
    ...mapActions("career", ["createCareer"]),
    ...mapActions("common", ["showLoading", "hideLoading", "showExactToast"]),

    handleDateRangeChange(value) {
      this.dateRange = {
        startDate: value.startDate,
        endDate: value.endDate
      }
    },

    async submit() {
      if (
        !this.form.title ||
        !this.form.location ||
        !this.form.work_type ||
        !this.dateRange.startDate ||
        !this.dateRange.endDate ||
        !this.form.description
      ) {
        this.showExactToast({
          message: "Please fill all required fields (*)",
          type: "error"
        })
        return
      }

      const startDate = new Date(this.dateRange.startDate)
      const endDate = new Date(this.dateRange.endDate)

      if (endDate <= startDate) {
        this.showExactToast({
          message: "End date must be greater than start date",
          type: "error"
        })
        return
      }

      this.showLoading()
      try {
        const payload = {
          title: this.form.title,
          location: this.form.location,
          work_type: this.form.work_type,
          start_date:
            dayjs(this.dateRange.startDate).format("YYYY-MM-DDTHH:mm:ss") + "Z",
          end_date:
            dayjs(this.dateRange.endDate).format("YYYY-MM-DDTHH:mm:ss") + "Z",
          description: this.form.description
        }

        await this.createCareer(payload)
        this.showExactToast({
          message: "Career created successfully",
          type: "success"
        })
        this.$router.push("/career")
      } catch (err) {
        console.error("Failed to create career:", err)
        this.showExactToast({
          message: err.response?.data?.message || "Failed to create career",
          type: "error"
        })
      } finally {
        this.hideLoading()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.date-range {
  &::v-deep .reportrange-text {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
    padding-left: 2.75rem;
    padding-right: 0.75rem;
    border-radius: 0.5rem;
    border-color: rgba(206, 213, 222, 1);
    border-width: 1px;
    border-style: solid;
    width: 100%;
    height: 50px;
    display: flex;
    align-items: center;
  }
  &::v-deep .vue-daterange-picker {
    display: block;
  }
}
</style>
